/**
 * Shared URL-safety helpers.
 *
 * Used to guard against reflected XSS / protocol-smuggling attacks (e.g. `javascript:`,
 * `data:`, `vbscript:` URIs) whenever a URL that originates from user input, the query
 * string, or an external/backend payload is placed into an `href`, `window.location`,
 * or similar navigation sink.
 *
 * Do NOT use this for URLs that are hardcoded string literals in source code - those
 * are not attacker-controlled and don't need validation.
 */

const ALLOWED_PROTOCOLS = ["http:", "https:"];
const ALLOWED_TARGETS = ["_blank", "_self"];

/**
 * Validates a URL and returns a safe, normalized href if it uses an allow-listed
 * protocol (http/https). Relative URLs are resolved against `base` (defaults to the
 * current origin). Returns `null` if the URL is invalid or uses a disallowed protocol.
 *
 * @param {string} rawUrl - The untrusted URL to validate.
 * @param {string} [base] - Base URL to resolve relative URLs against.
 * @returns {string|null} The safe absolute URL, or null if unsafe/invalid.
 */
export const getSafeUrl = (rawUrl, base = window.location.origin) => {
  if (!rawUrl) return null;
  try {
    const parsed = new URL(rawUrl, base);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol) ? parsed.href : null;
  } catch (e) {
    return null;
  }
};

/**
 * Validates that a URL is safe to navigate to / render in an href, without
 * needing the normalized result. Convenience wrapper around getSafeUrl.
 *
 * @param {string} rawUrl - The untrusted URL to validate.
 * @param {string} [base] - Base URL to resolve relative URLs against.
 * @returns {boolean} True if the URL is safe.
 */
export const isSafeUrl = (rawUrl, base = window.location.origin) => getSafeUrl(rawUrl, base) !== null;

/**
 * Allow-lists an anchor/link `target` value, falling back to a safe default
 * (`_blank`) for anything unrecognized (e.g. `_parent`, `_top`, or attacker-supplied
 * values from a query string).
 *
 * @param {string} rawTarget - The untrusted target value.
 * @param {string} [fallback] - Fallback target if rawTarget isn't allow-listed.
 * @returns {string} A safe target value.
 */
export const getSafeTarget = (rawTarget, fallback = "_blank") =>
  ALLOWED_TARGETS.includes(rawTarget) ? rawTarget : fallback;

// No functional changes required for UI testing.
